<template>
  <v-snackbar
    class="wrk-snackbar-root"
    :model-value="SnackBarComponent.SnackbarVmodel"
    :location="SnackBarComponent.Top ? 'top' : 'bottom'"
    :timeout="SnackBarComponent.timeout || 5000"
    color="transparent"
    variant="flat"
    elevation="0"
    rounded="0"
    transition="fade-transition"
    min-width="0"
    max-width="420"
  >
    <div :class="['wrk-toast', `wrk-toast--${tone}`]" role="status">
      <div class="wrk-toast__icon-wrap" aria-hidden="true">
        <v-icon :icon="resolvedIcon" size="16" class="wrk-toast__icon" />
      </div>
      <p class="wrk-toast__text">{{ SnackBarComponent.SnackbarText }}</p>
    </div>
  </v-snackbar>
</template>

<script>
export default {
  props: {
    SnackBarComponent: Object,
  },
  emits: ["update:model-value"],
  computed: {
    tone() {
      const raw = String(
        this.SnackBarComponent?.SnackbarColor || "",
      ).toLowerCase();
      if (
        raw.includes("green") ||
        raw.includes("success") ||
        raw === "success"
      ) {
        return "success";
      }
      if (raw.includes("red") || raw.includes("error") || raw === "error") {
        return "error";
      }
      if (
        raw.includes("orange") ||
        raw.includes("amber") ||
        raw.includes("warning") ||
        raw === "warning"
      ) {
        return "warning";
      }
      if (
        raw.includes("primary") ||
        raw.includes("purple") ||
        raw.includes("info") ||
        raw.includes("blue") ||
        raw === "info"
      ) {
        return "info";
      }
      return "neutral";
    },
    resolvedIcon() {
      if (this.SnackBarComponent?.icon) return this.SnackBarComponent.icon;
      const map = {
        success: "mdi-check-circle",
        error: "mdi-alert-circle-outline",
        warning: "mdi-alert-outline",
        info: "mdi-information-outline",
        neutral: "mdi-message-text-outline",
      };
      return map[this.tone] || map.neutral;
    },
  },
};
</script>

<style scoped>
.wrk-snackbar-root {
  z-index: 10050;
}

.wrk-snackbar-root :deep(.v-overlay__content),
.wrk-snackbar-root :deep(.v-snackbar__wrapper),
.wrk-snackbar-root :deep(.v-snackbar__content) {
  margin: 8px 12px !important;
  padding: 0 !important;
  min-height: 0 !important;
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
  border: none !important;
  overflow: visible !important;
}

.wrk-snackbar-root :deep(.v-snackbar--top .v-snackbar__wrapper) {
  margin-top: 16px !important;
}

.wrk-snackbar-root :deep(.v-snackbar--bottom .v-snackbar__wrapper) {
  margin-bottom: 16px !important;
}

.wrk-snackbar-root :deep(.v-snackbar__wrapper::before),
.wrk-snackbar-root :deep(.v-snackbar__wrapper::after) {
  display: none !important;
}

.wrk-snackbar-root :deep(.v-snackbar__content) {
  width: 100%;
}

.wrk-toast {
  --wrk-toast-accent: var(--sw-primary, #7b046c);
  position: relative;
  isolation: isolate;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 12px;
  overflow: hidden;
  backdrop-filter: var(--sw-snackbar-glass-blur);
  -webkit-backdrop-filter: var(--sw-snackbar-glass-blur);
  background: var(--sw-shell-glass-bg);
  border: 1px solid var(--sw-snackbar-glass-border);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.28);
  filter:
    drop-shadow(var(--sw-snackbar-glass-drop-1))
    drop-shadow(var(--sw-snackbar-glass-drop-2));
  animation: wrk-toast-in 0.28s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.wrk-toast::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: var(--sw-snackbar-glass-sheen);
  pointer-events: none;
  z-index: 0;
}

.wrk-toast::after {
  content: "";
  position: absolute;
  top: -28px;
  right: -18px;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: var(--sw-shell-glass-glow);
  pointer-events: none;
  z-index: 0;
}

.wrk-toast__icon-wrap,
.wrk-toast__text {
  position: relative;
  z-index: 1;
}

.wrk-toast__icon-wrap {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--sw-primary-rgb, 123, 4, 108), 0.08);
}

.wrk-toast__icon {
  color: var(--wrk-toast-accent) !important;
}

.wrk-toast__text {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.35;
  color: var(--sw-text, #111827);
  text-align: left;
}

.wrk-toast--success {
  --wrk-toast-accent: #0d9488;
  border-color: rgba(13, 148, 136, 0.28);
  background: rgba(240, 253, 250, 0.78);
}

.wrk-toast--success .wrk-toast__icon-wrap {
  background: rgba(13, 148, 136, 0.1);
}

.wrk-toast--error {
  --wrk-toast-accent: #dc2626;
  border-color: rgba(220, 38, 38, 0.26);
  background: rgba(254, 242, 242, 0.78);
}

.wrk-toast--error .wrk-toast__icon-wrap {
  background: rgba(220, 38, 38, 0.08);
}

.wrk-toast--warning {
  --wrk-toast-accent: #d97706;
  border-color: rgba(217, 119, 6, 0.28);
  background: rgba(255, 251, 235, 0.78);
}

.wrk-toast--warning .wrk-toast__icon-wrap {
  background: rgba(217, 119, 6, 0.1);
}

.wrk-toast--info {
  --wrk-toast-accent: var(--sw-primary, #7b046c);
  border-color: rgba(var(--sw-primary-rgb, 123, 4, 108), 0.22);
  background: rgba(253, 244, 255, 0.78);
}

.wrk-toast--neutral {
  --wrk-toast-accent: #6b7280;
  border-color: rgba(107, 114, 128, 0.22);
}

.wrk-toast--neutral .wrk-toast__icon-wrap {
  background: rgba(107, 114, 128, 0.1);
}

html[data-theme="dark"] .wrk-toast {
  background: rgba(34, 37, 47, 0.82);
}

html[data-theme="dark"] .wrk-toast--success {
  background: rgba(13, 148, 136, 0.22);
}

html[data-theme="dark"] .wrk-toast--error {
  background: rgba(220, 38, 38, 0.2);
}

html[data-theme="dark"] .wrk-toast--warning {
  background: rgba(217, 119, 6, 0.2);
}

html[data-theme="dark"] .wrk-toast--info {
  background: rgba(123, 4, 108, 0.18);
}

html[data-theme="dark"] .wrk-toast__text {
  color: var(--sw-text);
}

@keyframes wrk-toast-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .wrk-toast {
    animation: none;
  }
}
</style>
